import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  AlertTriangle,
  BadgeCheck,
  Camera,
  CheckCircle2,
  Copy,
  FileCheck2,
  Loader2,
  QrCode,
  Search,
  Send,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { Button } from '../../../components/ui/button';
import useIdentityVerification from './useIdentityVerification';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

function statusTone(status) {
  if (status === 'approved') return { label: 'Verified', color: '#16A34A', bg: '#DCFCE7' };
  if (status === 'pending') return { label: 'Under review', color: '#B45309', bg: '#FEF3C7' };
  if (status === 'declined') return { label: 'Declined', color: '#DC2626', bg: '#FEE2E2' };
  return { label: 'Not verified', color: '#6B7280', bg: '#F3F4F6' };
}

function parseQrValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.toLowerCase().startsWith('payemp-transfer:')) {
    const parts = raw.split(':');
    return parts[parts.length - 1] || raw;
  }
  return raw;
}

function stopTransferClickThrough(event) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  event?.nativeEvent?.stopImmediatePropagation?.();
}

function stopTransferPointerBubble(event) {
  event?.stopPropagation?.();
  event?.nativeEvent?.stopImmediatePropagation?.();
}

export default function WalletTransferPanel({ walletUsd, initialMode = '', onWalletChanged, onVerificationRequired, onClose }) {
  const [mode, setMode] = useState(initialMode || '');
  const { status, loading: loadingStatus, error: verificationError, submitting: kycSubmitting, uncertain: verificationUncertain, refresh: loadStatus, submit: submitIdentity } = useIdentityVerification();
  const [recipient, setRecipient] = useState('');
  const [recipientHit, setRecipientHit] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const kycStatus = status?.kyc?.status || 'not_submitted';
  const tone = statusTone(kycStatus);
  const isVerified = kycStatus === 'approved';
  const receiveUsername = status?.receive?.username || status?.user?.username || '';
  const receivePayload = status?.receive?.qr_payload || receiveUsername;

  useEffect(() => {
    if (initialMode) setMode(initialMode);
  }, [initialMode]);

  const proceedToVerification = (event) => {
    stopTransferClickThrough(event);
    if (onVerificationRequired) {
      onVerificationRequired(event);
      return;
    }
    setMode('kyc');
  };

  useEffect(() => {
    if (mode) loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => {
    if (!scanOpen) return undefined;
    let stopped = false;
    let timer = null;

    const start = async () => {
      setScanError('');
      if (!('BarcodeDetector' in window)) {
        setScanError('QR scanner is not supported on this browser. Type username or email instead.');
        return;
      }
      try {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        timer = window.setInterval(async () => {
          if (stopped || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            const raw = codes?.[0]?.rawValue;
            if (raw) {
              const parsed = parseQrValue(raw);
              setRecipient(parsed);
              setScanOpen(false);
              toast.success('QR scanned');
            }
          } catch {
            // Keep scanning.
          }
        }, 700);
      } catch {
        setScanError('Camera permission failed. Type username or email instead.');
      }
    };

    start();
    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [scanOpen]);

  const lookupRecipient = async () => {
    const ident = parseQrValue(recipient);
    if (!ident) {
      toast.error('Enter username or email');
      return null;
    }
    setBusy(true);
    try {
      const res = await api.post('/api/wallet-transfer/lookup', { identifier: ident });
      setRecipientHit(res.data?.player || null);
      return res.data?.player || null;
    } catch (err) {
      setRecipientHit(null);
      toast.error(err?.response?.data?.detail || 'Player not found in this tenant');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const sendTransfer = async () => {
    const amt = Number(amount || 0);
    if (!isVerified) {
      toast.error('Verification required');
      proceedToVerification();
      return;
    }
    if (!recipient.trim()) {
      toast.error('Enter receiver username or email');
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter amount');
      return;
    }
    if (amt > Number(walletUsd || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/api/wallet-transfer/send', {
        recipient: parseQrValue(recipient),
        amount: amt,
        note,
      });
      toast.success(`Sent ${money(amt)}`);
      setAmount('');
      setNote('');
      setRecipientHit(res.data?.transfer?.receiver_username ? recipientHit : null);
      await loadStatus();
      onWalletChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  const openScanner = (event) => {
    stopTransferClickThrough(event);
    setScanOpen(true);
  };

  const closeScanner = (event) => {
    stopTransferClickThrough(event);
    setScanOpen(false);
  };

  const handleLookupRecipient = (event) => {
    stopTransferClickThrough(event);
    lookupRecipient();
  };

  const handleSendTransfer = (event) => {
    stopTransferClickThrough(event);
    sendTransfer();
  };

  const submitKyc = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    if (await submitIdentity(fd)) {
      toast.success('Identity verification submitted');
      form.reset();
      setMode('receive');
    }
  };

  const copyReceive = async () => {
    try {
      await navigator.clipboard.writeText(receiveUsername);
      toast.success('Username copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleCopyReceive = (event) => {
    stopTransferClickThrough(event);
    copyReceive();
  };

  const header = useMemo(() => (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#475569' }}>
          Send & Receive
        </p>
      </div>
      <span
        className="px-2.5 py-1 rounded-full text-[11px] font-bold"
        style={{ background: tone.bg, color: tone.color }}
      >
        {tone.label}
      </span>
    </div>
  ), [tone.bg, tone.color, tone.label]);

  if (!mode) {
    return (
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button
          type="button"
          onClick={() => setMode('send')}
          className="h-12 rounded-xl display font-bold"
          style={{ background: 'linear-gradient(180deg,#3B82F6,#1D4ED8)', color: '#fff' }}
          data-testid="wallet-transfer-open-send"
        >
          <Send className="w-4 h-4 mr-1" /> Send
        </Button>
        <Button
          type="button"
          onClick={() => setMode('receive')}
          className="h-12 rounded-xl display font-bold"
          style={{ background: 'linear-gradient(180deg,#8B5CF6,#6D28D9)', color: '#fff' }}
          data-testid="wallet-transfer-open-receive"
        >
          <QrCode className="w-4 h-4 mr-1" /> Receive
        </Button>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-4 space-y-4"
      onPointerDown={stopTransferPointerBubble}
      onMouseDown={stopTransferPointerBubble}
      onTouchStart={stopTransferPointerBubble}
      onClick={stopTransferPointerBubble}
      style={{
        background: 'linear-gradient(135deg,#F8FAFC,#EEF2FF)',
        border: '1px solid rgba(99,102,241,0.22)',
        pointerEvents: 'auto',
      }}
      data-testid="wallet-transfer-panel"
    >
      {header}
      {verificationError && <p role="alert" className="text-sm text-red-700">{verificationError}</p>}
      {verificationUncertain && <p role="status" className="text-sm text-amber-800">Submission may already be saved. Check status before submitting again.</p>}
      <button type="button" disabled={loadingStatus || kycSubmitting} onClick={() => void loadStatus()} className="min-h-11 rounded-lg border border-slate-300 px-3 text-sm text-slate-800 disabled:opacity-60">{loadingStatus ? 'Checking status…' : 'Check verification status'}</button>
      {loadingStatus && !status ? (
        <div className="py-5 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
        </div>
      ) : (
        <>
          {!isVerified && mode !== 'kyc' && (
            <div className="rounded-xl p-3 flex items-start gap-3" style={{ background: '#FFF7ED', border: '1px solid rgba(249,115,22,0.25)' }}>
              <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#EA580C' }} />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm" style={{ color: '#9A3412' }}>Verification required</p>
                <p className="text-xs mt-0.5" style={{ color: '#9A3412' }}>
                  Verify your identity before sending or receiving wallet balance.
                </p>
                {kycStatus === 'declined' && status?.kyc?.reason && (
                  <p className="text-xs mt-1 font-semibold" style={{ color: '#B91C1C' }}>
                    Reason: {status.kyc.reason}
                  </p>
                )}
                <Button
                  type="button"
                  onPointerDown={stopTransferPointerBubble}
                  onMouseDown={stopTransferPointerBubble}
                  onTouchStart={stopTransferPointerBubble}
                  onClick={proceedToVerification}
                  className="mt-2 h-9 rounded-lg font-bold"
                  style={{ background: '#0F172A', color: '#fff' }}
                  data-testid="wallet-transfer-verify-proceed"
                >
                  <FileCheck2 className="w-4 h-4 mr-1" /> Click here to proceed
                </Button>
              </div>
            </div>
          )}

          {mode === 'send' && (
            <div className="space-y-3">
              <div
                className="rounded-xl p-3 flex items-start gap-3"
                style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.25)' }}
                data-testid="wallet-transfer-send-warning"
              >
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
                <p className="text-xs leading-relaxed font-semibold" style={{ color: '#991B1B' }}>
                  Double-check the receiver username before sending. Once funds are transferred, they cannot be reversed.
                </p>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <input
                  value={recipient}
                  onChange={(e) => {
                    setRecipient(e.target.value);
                    setRecipientHit(null);
                  }}
                  placeholder="Username or email"
                  className="h-11 rounded-xl px-3 text-sm bg-white border border-slate-200 text-slate-900 outline-none focus:border-blue-500"
                  data-testid="wallet-transfer-recipient"
                />
                <button
                  type="button"
                  onPointerDown={stopTransferPointerBubble}
                  onMouseDown={stopTransferPointerBubble}
                  onTouchStart={stopTransferPointerBubble}
                  onClick={openScanner}
                  className="h-11 px-3 rounded-xl bg-slate-900 text-white flex items-center gap-1 text-sm font-bold"
                >
                  <Camera className="w-4 h-4" /> Scan
                </button>
              </div>
              {scanOpen && (
                <div className="rounded-xl p-3 space-y-2 bg-slate-950 text-white">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">Scan receiver QR</span>
                    <button
                      type="button"
                      onPointerDown={stopTransferPointerBubble}
                      onMouseDown={stopTransferPointerBubble}
                      onTouchStart={stopTransferPointerBubble}
                      onClick={closeScanner}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {scanError ? (
                    <p className="text-xs text-amber-200">{scanError}</p>
                  ) : (
                    <video ref={videoRef} className="w-full rounded-lg bg-black aspect-video object-cover" muted playsInline />
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  onPointerDown={stopTransferPointerBubble}
                  onMouseDown={stopTransferPointerBubble}
                  onTouchStart={stopTransferPointerBubble}
                  onClick={handleLookupRecipient}
                  disabled={busy}
                  className="h-10 rounded-lg font-bold"
                  style={{ background: '#E0E7FF', color: '#3730A3' }}
                >
                  <Search className="w-4 h-4 mr-1" /> Check Receiver
                </Button>
                {recipientHit && (
                  <div className="flex-1 rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-200 min-w-0">
                    <p className="text-xs font-bold text-emerald-700 truncate">
                      {recipientHit.full_name || recipientHit.username || recipientHit.email}
                    </p>
                    <p className="text-[11px] text-emerald-700 truncate">@{recipientHit.username || 'player'}</p>
                  </div>
                )}
              </div>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder={`Amount, available ${money(walletUsd)}`}
                className="h-11 w-full rounded-xl px-3 text-sm bg-white border border-slate-200 text-slate-900 outline-none focus:border-blue-500"
                data-testid="wallet-transfer-amount"
              />
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note optional"
                className="h-11 w-full rounded-xl px-3 text-sm bg-white border border-slate-200 text-slate-900 outline-none focus:border-blue-500"
              />
              <Button
                type="button"
                onPointerDown={stopTransferPointerBubble}
                onMouseDown={stopTransferPointerBubble}
                onTouchStart={stopTransferPointerBubble}
                onClick={handleSendTransfer}
                disabled={busy}
                className="w-full h-11 rounded-xl display font-bold"
                style={{ background: isVerified ? 'linear-gradient(180deg,#22C55E,#16A34A)' : '#CBD5E1', color: '#fff' }}
                data-testid="wallet-transfer-send-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Balance
              </Button>
            </div>
          )}

          {mode === 'receive' && (
            <div className="space-y-3 text-center">
              {isVerified && (
                <>
                  <div className="inline-flex p-3 rounded-2xl bg-white border border-slate-200">
                    <QRCodeCanvas value={receivePayload} size={170} includeMargin />
                  </div>
                  <div className="rounded-xl p-3 bg-white border border-slate-200 flex items-center justify-between gap-3 text-left">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Username</p>
                      <p className="font-black text-slate-900 truncate">@{receiveUsername}</p>
                    </div>
                    <button
                      type="button"
                      onPointerDown={stopTransferPointerBubble}
                      onMouseDown={stopTransferPointerBubble}
                      onTouchStart={stopTransferPointerBubble}
                      onClick={handleCopyReceive}
                      className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {mode === 'kyc' && !['pending', 'approved'].includes(kycStatus) && (
            <form onSubmit={submitKyc} className="space-y-3">
              <fieldset disabled={kycSubmitting || loadingStatus || verificationUncertain || !status} className="min-w-0 space-y-3">
              <div className="rounded-xl p-3 bg-white border border-slate-200 flex items-start gap-2">
                <BadgeCheck className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600">
                  Upload a clear selfie/photo plus a valid document (up to 8 MB per file). Admin will approve or decline with reason.
                </p>
              </div>
              <select name="document_type" className="h-11 w-full rounded-xl px-3 text-sm bg-white border border-slate-200 text-slate-900">
                <option value="driver_license">Driver license</option>
                <option value="passport">Passport</option>
                <option value="id_card">ID card</option>
              </select>
              {[
                ['identity_photo', 'Your photo / selfie'],
                ['document_front', 'Document front'],
                ['document_back', 'Document back'],
              ].map(([name, label]) => (
                <label key={name} className="block rounded-xl p-3 bg-white border border-slate-200">
                  <span className="flex items-center gap-2 text-xs font-bold text-slate-600 mb-2">
                    <Upload className="w-4 h-4" /> {label}
                    {name === 'document_back' && <span className="font-medium text-slate-400">(required for DL/ID)</span>}
                  </span>
                  <input
                    name={name}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    required={name !== 'document_back'}
                    className="block w-full text-xs text-slate-600"
                  />
                </label>
              ))}
              <textarea
                name="notes"
                placeholder="Notes optional"
                className="min-h-[72px] w-full rounded-xl px-3 py-2 text-sm bg-white border border-slate-200 text-slate-900 outline-none focus:border-blue-500"
              />
              <Button
                type="submit"
                disabled={kycSubmitting || loadingStatus || verificationUncertain || !status}
                className="w-full h-11 rounded-xl display font-bold"
                style={{ background: 'linear-gradient(180deg,#0F172A,#334155)', color: '#fff' }}
              >
                {kycSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Submit Identity Review
              </Button>
              </fieldset>
            </form>
          )}
          {mode === 'kyc' && ['pending', 'approved'].includes(kycStatus) && <p role="status" className="text-sm text-slate-700">{kycStatus === 'pending' ? 'Your ID is under review. Check status for an update.' : 'Your identity is verified.'}</p>}
        </>
      )}
    </div>
  );
}
